import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {gsap} from "gsap";
import {KennethMustDie} from "../../KennethMustDie";
import {KMDSymbol} from "../../reels/KMDSymbol";
import {SPECIAL_SYMBOL_NAMES} from "../../server/IKMDData";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {KMDGameMode} from "../../server/KMDGameMode";
import {KMDSound} from "../../sound/KMDSound";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class KMDFSIntro extends PIXI.Container implements StageLayer {

    private _introTexture: PIXI.spine.Spine;
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _hideIntroTl: gsap.core.Timeline;
    private _highlightGfx: PIXI.Sprite;
    private _fullScreenClicker: PIXI.Graphics;
    private _whiteGlow: PIXI.Sprite;

    constructor() {
        super();
        this.name = "KMDFSIntro";

        this.init();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public showIntro(callback: () => void): void {
        const showIntroTL: gsap.core.Timeline = gsap.timeline({
            delay: 0.2
        });

        if (this.checkForUpgrade()) {
            showIntroTL.add(KennethMustDie.additionalFSAward.showAdditionalFS(this.getExtraFS())); // #358

            // #485
            showIntroTL.add(this.getS2AndXHTriggerAnimation());
            showIntroTL.add(this.animateWhiteGlow());
            showIntroTL.add(this.getUpgradeTransition(this.getOutroNameForUpgrade()));
        } else {
            showIntroTL.add(() => {
                KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_ACTIVATION);

                // #503, #505
                KennethMustDie.soundPlayer.fadeMusic({
                    name: "",
                    duration: 100,
                    to: 0
                });
            });

            // #485
            [1, 2, 3].forEach((reelId: number) => {
                const sym: KMDSymbol = (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(KennethMustDie.model.s1Positions[reelId]).positionable as KMDSymbol);

                // #402
                sym.updateTexture(SPECIAL_SYMBOL_NAMES.S);

                // #485
                if (!KennethMustDie.model.data.wasFeatureBuy) {
                    showIntroTL.add(KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(reelId, KennethMustDie.model.s1Positions[reelId] + 1, sym.resizeLevel), 0);
                }
            });
            showIntroTL.add(this.getS2AndXHTriggerAnimation(), 0);
            showIntroTL.add(this.animateWhiteGlow());

            showIntroTL.add(() => {
                this.setIntroTexture();

                KennethMustDie.blackBg.show(0.8);

                KennethMustDie.xHoleBlackBg.stopFlicker();
                KennethMustDie.nearWinAnticipation.stopFire();

                // #410, #408
                this.playIntroSound();
            });
            showIntroTL.add(KennethMustDie.createTimelineForAnimation(this._introTexture, 0, "animation", false));
        }

        // #525
        if (KennethMustDie.model.gameData.wasFeatureBuy && KennethMustDie.autoPlayPlugin.isAutoplayRound) {
            showIntroTL.add(() => {
                this._clickCallback = callback;
                this.onButtonClick();
            });
        } else {
            showIntroTL.add(() => {
                this._button.alpha = 0;
                this._button.visible = true;

                this._clickCallback = callback;
            });
            showIntroTL.to(this._button, {
                duration: 0.5,
                alpha: 1
            }, "<");
            showIntroTL.add(() => {
                this._button.enable(true);
            });
        }
    }

    protected getFeatureExplainTl(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this.getWildFeatureExplain());

        return tl;
    }

    protected getAssetName(): string {
        return ("fsIntro");
    }

    protected playIntroSound(): void {
        KennethMustDie.soundPlayer.playSound(KMDSound.FS_INTRO_IMPACT);

        KennethMustDie.soundPlayer.switchMusic("fsIntro");
    }

    protected playBtnSound(): void {
        KennethMustDie.soundPlayer.stopSound(KMDSound.FS_INTRO_IMPACT);

        KennethMustDie.soundPlayer.playSound(KMDSound.BTN_START_FS);
    }

    protected checkForUpgrade(): boolean {
        return false;
    }

    protected getOutroNameForUpgrade(): string {
        return "";
    }

    protected getPreTransition(): void {
    }

    protected getTransition(): gsap.core.Timeline {
        return gsap.timeline();
    }

    // #358
    protected getExtraFS(): number {
        return -1;
    }

    private init(): void {
        this._whiteGlow = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("effects/white_glow")[0]);
        this._whiteGlow.visible = false;
        this._whiteGlow.anchor.set(0.5);
        this._whiteGlow.position.set(360);

        this._introTexture = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(this.getAssetName()));
        this._introTexture.position.set(360);
        this._introTexture.visible = false;

        this._button = new ContinueButton();
        this._button.addClickCallback(() => {
            this.onButtonClick();

            // #409
            this.playBtnSound();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.visible = false;
        this.onResize();

        this._highlightGfx = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("outlineRectangle")[0]);
        this._highlightGfx.anchor.set(0.5);
        this._highlightGfx.visible = false;
        this._highlightGfx.position.set(360, KennethMustDie.MAIN_REELS_TOP + KennethMustDie.SYMBOL_HEIGHT);

        this._fullScreenClicker = new PIXI.Graphics().beginFill(0xffffff, 1).drawRect(-480, -480, 1680, 1680).endFill();
        this._fullScreenClicker.visible = false;

        this.addChild(this._whiteGlow, this._introTexture, this._button, this._highlightGfx, this._fullScreenClicker);
    }

    private setIntroTexture(): void {
        this._introTexture.visible = true;
        this._introTexture.updateTransform();

        this._introTexture.state.clearTracks();
        this._introTexture.skeleton.setToSetupPose();
        this._introTexture.skeleton.setSlotsToSetupPose();
        this._introTexture.state.setEmptyAnimation(0, 0);
    }

    private getS2AndXHTriggerAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (KennethMustDie.model.s2Feature.originReelId > -1) {
            const s2Sym: KMDSymbol = KennethMustDie.reelView.reelModels[4].getStopSymbolAt(KennethMustDie.model.s2Feature.originSymId - 1).positionable as KMDSymbol;

            s2Sym.updateTexture(SPECIAL_SYMBOL_NAMES.S2);
            // #485
            if (!KennethMustDie.model.data.wasFeatureBuy) {
                tl.add(KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(4, KennethMustDie.model.s2Feature.originSymId + 1, 0), 0);
            }
        }

        if (KennethMustDie.model.gameData.nextMode === KMDGameMode.FREESPIN_SUPER) {
            const xHSym: KMDSymbol = KennethMustDie.reelView.reelModels[2].getStopSymbolAt(1).positionable as KMDSymbol;

            xHSym.originalName = SPECIAL_SYMBOL_NAMES.XH;
            xHSym.updateAnimation(SPECIAL_SYMBOL_NAMES.XH);
            // #485
            if (!KennethMustDie.model.data.wasFeatureBuy) {
                tl.add(KennethMustDie.nearWinAnticipation.playXHAnticipateFireAnimation(xHSym.resizeLevel), 0);
            }
        }

        return tl;
    }

    // #485
    private animateWhiteGlow(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (KennethMustDie.model.data.wasFeatureBuy) {
            return tl;
        }

        tl.add(() => {
            this._whiteGlow.alpha = 0;
            this._whiteGlow.visible = true;
        });
        tl.to(this._whiteGlow, {
            duration: 8 / 30,
            alpha: 1
        });
        tl.to(this._whiteGlow.scale, {
            duration: 8 / 30,
            x: 6.3,
            y: 6.3
        }, "<");

        tl.add(() => {
            for (let i: number = 0; i < KennethMustDie.REELS_NUM; i++) {
                KennethMustDie.nearWinAnticipation.stopAnticipationFire(i);
            }
            KennethMustDie.nearWinAnticipation.stopXHAnticipationFire();

            this._whiteGlow.visible = false;
        });

        return tl;
    }

    private getUpgradeTransition(outroSpineName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            outro: PIXI.Sprite = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("Intro/" + outroSpineName)[0]),
            duration: number = (21 / 30);

        this._introTexture.alpha = 0;

        outro.position.set(360);
        outro.anchor.set(0.5);

        tl.add(() => {
            this.addChildAt(outro, 0);

            this.setIntroTexture();
            this._introTexture.state.setAnimation(0, "animation", false).timeScale = 100;

            KennethMustDie.blackBg.show(0.8);

            // #389
            KennethMustDie.hideBonusGameStickyItems();

            // #410, #408
            this.playIntroSound();
        }, 0);
        tl.to(outro, {
            duration: duration,
            alpha: 0
        }, 0);
        tl.to(this._introTexture, {
            duration: duration,
            alpha: 1
        }, 0);

        tl.add(() => {
            this.removeChild(outro);
        });

        return tl;
    }

    private onButtonClick(): void {
        const hideIntroTL: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    this.finishUp();
                }
            }),
            nextMode: string = KennethMustDie.model.gameData.nextMode,
            duration: number = 0.5;

        KennethMustDie.backgroundElements.setMode(nextMode);

        // #499
        KennethMustDie.setFakeReelSet();

        // #281
        this.getPreTransition();

        this._button.enable(false);

        // #383
        this._fullScreenClicker.alpha = 0;
        this._fullScreenClicker.interactive = true;
        this._fullScreenClicker.buttonMode = true;
        this._fullScreenClicker.visible = true;
        this._fullScreenClicker.once('pointerdown', () => {
            this.finishUp();

            this.skip();
        });

        hideIntroTL.add(() => {
            if (KennethMustDie.model.isRestore) {
                KennethMustDie.keypadPlugin.setZeroBetSpinCounter(Math.max(KennethMustDie.model.data.freespinsLeft, 0));
            } else {
                KennethMustDie.keypadPlugin.setZeroBetSpinCounter(KennethMustDie.model.gameData.freespinsLeft);
            }

            KennethMustDie.xBet.toggleVisibility(false);
            KennethMustDie.limitCapButton.toggleVisibility(false);

            // #505
            if (nextMode !== KMDGameMode.FREESPIN_COOL) {
                KennethMustDie.soundPlayer.switchMusic(nextMode);
            }

            KennethMustDie.xWaysReveal.hideAll();
        }, 0);
        if (nextMode === KMDGameMode.FREESPIN_COOL) {
            hideIntroTL.add(KennethMustDie.blackBg.hide(duration), 0);
        }

        hideIntroTL.to([this._button, this._introTexture], {
            duration: duration,
            alpha: 0
        }, 0);

        // #281
        hideIntroTL.add(this.getTransition());

        hideIntroTL.add(() => {
            this._button.visible = false;
            this._introTexture.visible = false;
            this._introTexture.alpha = 1;

            // #276, #339 -> only if we aren't entering via an upgrade
            if (KennethMustDie.model.gameData.mode.indexOf("NORMAL") > -1) {
                KennethMustDie.multipliersChain.onBonusGameIntro();
            }
        });

        if (nextMode === KMDGameMode.FREESPIN_COOL) {
            hideIntroTL.add(() => {
                KennethMustDie.blackBg.show(0.8);
            });
        }
        hideIntroTL.to(this, {
            duration: 0.1
        });

        // #560
        const isLiteSFSBought: boolean = (KennethMustDie.buyFeatureName === "LITE_SUPER_FREESPIN");
        if ((!isLiteSFSBought) || (isLiteSFSBought && KennethMustDie.liteSFSBuyCount < 3)) {
            hideIntroTL.add(this.getFeatureExplainTl());
        }
        if (isLiteSFSBought) {
            KennethMustDie.liteSFSBuyCount++;
        }

        hideIntroTL.add(() => {
            this._clickCallback();

            this._highlightGfx.visible = false;
        });
        hideIntroTL.add(KennethMustDie.blackBg.hide(0.2));

        this._hideIntroTl = hideIntroTL;
    }

    private getWildFeatureExplain(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            wild: KMDSymbol = new KMDSymbol(SPECIAL_SYMBOL_NAMES.W),
            lock: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("locks")),
            symId: number = MathHelper.randomNumberInRange(0, 2);
        let reelId: number = MathHelper.randomNumberInRange(1, 3);

        if ((KennethMustDie.model.gameData.nextMode === KMDGameMode.FREESPIN_SUPER) && (reelId === 2) && (symId === 1)) {
            reelId = ArrayHelper.getRandomValue([1, 3]); // excluding reel 2
        }

        wild.position.set(((reelId * KennethMustDie.SYMBOL_WIDTH) + KennethMustDie.SYMBOL_WIDTH / 2), (symId * KennethMustDie.SYMBOL_HEIGHT) + KennethMustDie.MAIN_REELS_TOP);
        lock.position = wild.position;

        tl.to(this, {
            duration: 0.1
        });
        tl.add(() => {
            this._highlightGfx.visible = true;

            this.addChild(wild, lock);

            // #479
            KennethMustDie.soundPlayer.playSound(KMDSound.SYM_CHAIN_ADD);
        });
        tl.add(KennethMustDie.createTimelineForAnimation(lock, 0, "locked_150_start", false));
        tl.to(this, {
            duration: 0.4
        });

        tl.to([wild, lock], {
            duration: 0.2,
            alpha: 0
        });

        tl.add(() => {
            this.removeChild(wild, lock);

            wild.destroy();
        });

        return tl;
    }

    // #383
    private finishUp(): void {
        this._fullScreenClicker.visible = false;
        this._fullScreenClicker.interactive = false;
        this._fullScreenClicker.buttonMode = false;
    }

    // #383
    private skip(): void {
        if (this._hideIntroTl) {
            this._hideIntroTl.timeScale(5);
        }
    }
}